import { useEffect, useState } from "react";

import { FileWithPath, useDropzone } from "react-dropzone";
import UploadIcon from "@/assets/icons/UploadIcon.svg";
import FileIcon from "@/assets/icons/FileIconFill.svg";
import DeleteIcon from "@/assets/icons/DeleteIconGray.svg";
import LoadingStrip from "@/components/ui/LoadinStrip";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { formatFileSize } from "@/utils/formatFileSize";
interface FileUploadProps {
  height?: string;
  videoId?: number;
  onFilesUploaded?: (files: FileDetails[]) => void;
  maxFiles?: number;
  defaultValue?: FileDetails[];
  maxSize?: number; // in bytes
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  showSelectedFiles?: boolean;
  useExternalState?: boolean;
  externalFiles?: FileDetails[];
  setExternalFiles?: React.Dispatch<React.SetStateAction<FileDetails[]>>;
  setVideosAreUpload?: React.Dispatch<React.SetStateAction<string | null>>;
  /** Fires when S3 batch upload starts (true) or finishes (false). Use to disable submit while uploads are in flight. */
  onUploadingChange?: (busy: boolean) => void;
}

const FileUploadField: React.FC<FileUploadProps> = ({
  height = "196px",
  onFilesUploaded,
  maxFiles = 10,
  showSelectedFiles = true,
  defaultValue = [],
  useExternalState = false,
  externalFiles = [],
  setExternalFiles,
  setVideosAreUpload,
  maxSize = 100 * 1024 * 1024, // 100MB default
  accept = {
    "image/jpeg": [],
    "application/pdf": [],
    "video/mp4": [".mp4"],
    "video/quicktime": [".mov"],
    "image/gif": [],
  },
  clearFiles = false,
  onUploadingChange,
}) => {
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(defaultValue);
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const files = useExternalState ? externalFiles : localFiles;
  const setFiles = useExternalState ? setExternalFiles : setLocalFiles;

  const acceptedFileTypes = Object.keys(accept)
    .map((type) => {
      const [mainType, subType] = type.split("/");
      return mainType === "application"
        ? subType.toUpperCase()
        : subType.toUpperCase();
    })
    .join(", ");

  useEffect(() => {
    if (clearFiles && setFiles) {
      setFiles([]);
      onFilesUploaded?.([]);
      onUploadingChange?.(false);
    }
  }, [clearFiles, setFiles, onFilesUploaded, onUploadingChange]);

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading || !setFiles) return;
    setUploading(true);
    onUploadingChange?.(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - files.length);

    const uniqueNumber = new Date().getTime();
    for (const file of newFiles) {
      const fileDetails: FileDetails = {
        name: `${uniqueNumber.toString()}-${file.name}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      setFiles((prevFiles) => {
        const next = [...prevFiles, fileDetails];
        queueMicrotask(() => onFilesUploaded?.(next));
        return next;
      });

      try {
        setShowProgress(fileDetails.name);
        setVideosAreUpload?.(fileDetails.name);
        const uploadedFile = await UploadFileToS3(fileDetails);

        const updatedFile = {
          ...fileDetails,
          live_url: uploadedFile.live_url,
          uploading: false,
        };

        setFiles((prevFiles) => {
          const next = prevFiles.map((f) =>
            f.name === fileDetails.name ? updatedFile : f
          );
          queueMicrotask(() => onFilesUploaded?.(next));
          return next;
        });

        setVideosAreUpload?.(null);
        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        setVideosAreUpload?.(null);
        console.error(`Failed to upload file: ${file.name}`, error);
        setFiles((prevFiles) => {
          const next = prevFiles.filter((f) => f.name !== fileDetails.name);
          queueMicrotask(() => onFilesUploaded?.(next));
          return next;
        });
      }
    }

    setUploading(false);
    onUploadingChange?.(false);
  };

  const handleRemoveFile = (index: number) => {
    if (!setFiles) return;
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesUploaded?.(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
  });

  return (
    <div>
      {files.length < maxFiles && (
        <div
          {...getRootProps()}
          className={`mx-auto border-dashed bg-[#F9F9F9] relative h-[${height}] border-[#EAECF0] rounded-[8px] text-center py-3 flex flex-col justify-center items-center cursor-pointer`}
        >
          <input
            {...getInputProps()}
            className="w-full absolute top-0 left-0 h-full opacity-0"
          />
          <UploadIcon />
          <p className="text-caption-large pt-3 text-[#475467]">
            <span className="text-[#ED5E00] font-semibold">
              Click to upload
            </span>{" "}
            or drag and drop
          </p>
          <p className="text-caption2-regular text-[#475467] pt-1">
            {acceptedFileTypes} (max. {maxSize / (1024 * 1024)} MB)
          </p>
        </div>
      )}

      {showSelectedFiles && (
        <>
          {files.map((file, index) => (
            <div
              key={index}
              className="border-[#EAECF0] w-full my-4 overflow-hidden flex justify-between items-start border rounded-[8px] px-4 py-4 "
            >
              <div className="flex w-full gap-x-4 items-start">
                <div>
                  <FileIcon />
                </div>
                <div className="w-full">
                  <p className="font-medium text-[#344054] text-caption-large">
                    {file?.name?.length > 40
                      ? `${file?.name?.slice(0, 40)}...`
                      : file?.name}
                  </p>
                  <p className="text-[#475467] text-caption-large">
                    {formatFileSize(file.size)}
                  </p>
                  <div className="pt-1">
                    {showProgress === file.name && <LoadingStrip />}
                  </div>
                  {file.live_url && (
                    <a
                      href={file.live_url}
                      target="_blank"
                      className="text-caption-regular font-medium pt-1"
                      rel="noopener noreferrer"
                    >
                      View file
                    </a>
                  )}
                </div>
              </div>
              <div className="flex justify-end w-fit relative">
                <button onClick={() => handleRemoveFile(index)}>
                  <DeleteIcon />
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default FileUploadField;
// Import your icons and utility functions

// interface FileUploadProps {
//   videoId?: number;
//   onFilesUploaded: (files: FileDetails[]) => void;
//   maxFiles?: number;
//   defaultValue?: FileDetails[];
//   maxSize?: number; // in bytes
//   accept?: { [key: string]: string[] };
//   clearFiles?: boolean;
//   showSelectedFiles?: boolean;
//   setVideosAreUpload? : React.Dispatch<React.SetStateAction<string | null>>
// }

// const FileUploadField = ({
//   onFilesUploaded,
//   maxFiles = 10,
//   showSelectedFiles = true,
//   defaultValue,
//   setVideosAreUpload,
//   maxSize = 100 * 1024 * 1024, // 50MB default
//   accept = {
//     "image/jpeg": [],
//     "application/pdf": [],
//     "video/mp4": [],
//     "image/gif": [],
//   },
//   clearFiles = false,
// }: FileUploadProps) => {
//   const [localFiles, setLocalFiles] = useState<FileDetails[]>(
//     defaultValue || []
//   );
//   const [showProgress, setShowProgress] = useState<string | null>(null);
//   const [uploading, setUploading] = useState<boolean>(false);

//   // Create a string for accepted file types
//   const acceptedFileTypes = Object.keys(accept)
//     .map((type) => {
//       const [mainType, subType] = type.split("/");
//       return mainType === "application"
//         ? subType.toUpperCase()
//         : subType.toUpperCase();
//     })
//     .join(", ");

//   useEffect(() => {
//     if (clearFiles) {
//       setLocalFiles([]); // Clear the files
//     }
//   }, [clearFiles]);

//   const onDrop = async (acceptedFiles: FileWithPath[]) => {
//     if (uploading) return;
//     setUploading(true);

//     const newFiles = acceptedFiles.slice(0, maxFiles - localFiles.length);
//     const updatedFiles = [...localFiles];
//     const uniqueNumber = new Date().getTime();
//     for (const file of newFiles) {
//       const fileDetails: FileDetails = {
//         name: `${file.name}-${uniqueNumber.toString()}`,
//         size: file.size,
//         type: file.type,
//         lastModified: file.lastModified,
//         webkitRelativePath: file.webkitRelativePath,
//         local_url: URL.createObjectURL(file),
//         live_url: null,
//       };

//       setLocalFiles((prevFiles) => [...prevFiles, fileDetails]);

//       try {
//         setShowProgress(fileDetails.name);
//         setVideosAreUpload?.(fileDetails.name)
//         const uploadedFile = await UploadFileToS3(fileDetails);

//         const updatedFile = {
//           ...fileDetails,
//           live_url: uploadedFile.live_url,
//           uploading: false,
//         };
//         updatedFiles.push(updatedFile);

//         setLocalFiles((prevFiles) =>
//           prevFiles.map((f) => (f.name === fileDetails.name ? updatedFile : f))
//         );
//         setVideosAreUpload?.(null)

//         setShowProgress(null);
//       } catch (error) {
//         setShowProgress(null);
//         setVideosAreUpload?.(null)

//         console.error(`Failed to upload file: ${file.name}`, error);
//       }
//     }

//     setUploading(false);
//     onFilesUploaded(updatedFiles);

//     // Optional: If you want to dispatch to Redux
//     // dispatch(addScriptDetail({ field, videoId, value: updatedFiles }));
//   };

//   const { getRootProps, getInputProps } = useDropzone({
//     onDrop,
//     multiple: true,
//     maxSize,
//     accept,
//   });

//   const handleRemoveFile = (index: number) => {
//     const updatedFiles = localFiles.filter((_, i) => i !== index);
//     setLocalFiles(updatedFiles);
//     onFilesUploaded(updatedFiles);
//   };

//   return (
//     <div>
//       {localFiles.length < maxFiles && (
//         <div
//           {...getRootProps()}
//           className="mx-auto border-dashed bg-[#F9F9F9] relative h-[196px]  border-[#EAECF0] rounded-[8px] text-center py-3 flex flex-col justify-center items-center cursor-pointer"
//         >
//           <input
//             {...getInputProps()}
//             className="w-full absolute top-0 left-0 h-full opacity-0"
//           />
//           <UploadIcon />
//           <p className="text-caption-large pt-3 text-[#475467]">
//             <span className="text-[#ED5E00] font-semibold">
//               Click to upload
//             </span>{" "}
//             or drag and drop
//           </p>
//           <p className="text-caption2-regular text-[#475467] pt-1">
//             {acceptedFileTypes} (max. {maxSize / (1024 * 1024)} MB)
//           </p>
//         </div>
//       )}

//       {showSelectedFiles && (
//         <>
//           {localFiles.map((file, index) => (
//             <div
//               key={index}
//               className="border-[#EAECF0] w-full my-4 overflow-hidden flex justify-between items-start border rounded-[8px] px-4 py-4 "
//             >
//               <div className="flex w-full gap-x-4 items-start">
//                 <div>
//                   {" "}
//                   <FileIcon />
//                 </div>
//                 <div className="w-full">
//                   <p className="font-medium text-[#344054] text-caption-large">
//                     {file.name.length > 40
//                       ? `${file.name.slice(0, 40)}...`
//                       : file.name}
//                   </p>
//                   <p className="text-[#475467] text-caption-large">
//                     {(file.size / 1024).toFixed(2)} KB
//                   </p>
//                   {showProgress === file.name && <LoadingStrip />}
//                   {file.live_url && (
//                     <a
//                       href={file.live_url}
//                       target="_blank"
//                       className="text-caption-regular font-medium pt-1"
//                       rel="noopener noreferrer"
//                     >
//                       View file
//                     </a>
//                   )}
//                 </div>
//               </div>
//               <div className="  flex justify-end  w-fit relative ">
//                 <button onClick={() => handleRemoveFile(index)}>
//                   <DeleteIcon />
//                 </button>
//               </div>
//             </div>
//           ))}
//         </>
//       )}
//     </div>
//   );
// };

// export default FileUploadField;
